import { preloadHtmlImage, makeCanvas } from "../utils";
import { AbstractImageAdapter } from "./AbstractImageAdapter";
import type { ImageAdapter } from "./ImageAdapter";
import type { Color } from "../types";

/**
 * Canvas element or offscreen canvas.
 */
export type BrowserCanvas = HTMLCanvasElement | OffscreenCanvas;

/**
 * Browser canvas rendering context 2d.
 */
export type BrowserCanvasContext2d =
  | CanvasRenderingContext2D
  | OffscreenCanvasRenderingContext2D;

/**
 * Returns canvas rendering context.
 *
 * @param canvas
 */
function getCanvasContext(canvas: BrowserCanvas): BrowserCanvasContext2d {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    throw new Error("Couldn't get canvas context");
  }
  return context as BrowserCanvasContext2d;
}

/**
 * ImageAdapter implementation for distortion using HTML5 Canvas objects.
 */
export class Canvas
  extends AbstractImageAdapter<BrowserCanvas>
  implements ImageAdapter<BrowserCanvas>
{
  /**
   * Browser canvas (HTMLCanvasElement or OffscreenCanvas) with image resource.
   */
  protected canvas: BrowserCanvas;

  /**
   * ImageData object to work with.
   */
  protected imageData: ImageData;

  /**
   * ImageData.data array stored for faster access.
   */
  protected data: Uint8ClampedArray;

  /**
   * Canvas constructor.
   *
   * @param canvas HTML5 Canvas or offscreen canvas with image data.
   */
  constructor(canvas: BrowserCanvas) {
    super(canvas.width, canvas.height);
    this.canvas = canvas;
    this.imageData = getCanvasContext(canvas).getImageData(
      0,
      0,
      canvas.width,
      canvas.height
    );
    this.data = this.imageData.data;
  }

  /**
   * Creates new instance using CanvasImageSource.
   *
   * @param image
   */
  static createFromImage(
    image: CanvasImageSource & { width: number; height: number }
  ): Canvas {
    const canvas = makeCanvas(image.width, image.height);
    getCanvasContext(canvas).drawImage(image, 0, 0);
    return new Canvas(canvas);
  }

  /**
   * Asynchronously creates Canvas instance from image url.
   *
   * @param url
   */
  static async createFromUrl(url: string): Promise<Canvas> {
    const image = await preloadHtmlImage(url);
    return this.createFromImage(image);
  }

  /**
   * Asynchronously creates Canvas instance from Blob or File.
   *
   * @param blob
   */
  static async createFromBlob(blob: Blob): Promise<Canvas> {
    const bitmap = await createImageBitmap(blob);
    return this.createFromImage(bitmap);
  }

  /**
   * @inheritDoc
   */
  getPixelColor(x: number, y: number): Color {
    const offset = (y * this.width + x) * 4;
    return [
      this.data[offset],
      this.data[offset + 1],
      this.data[offset + 2],
      this.data[offset + 3],
    ];
  }

  /**
   * @inheritDoc
   */
  setPixelColor(x: number, y: number, color: Color): void {
    const offset = (y * this.width + x) * 4;
    this.data[offset] = color[0];
    this.data[offset + 1] = color[1];
    this.data[offset + 2] = color[2];
    this.data[offset + 3] = color[3];
  }

  /**
   * @inheritDoc
   */
  getAverageColor(): Color {
    const canvas = makeCanvas(1, 1);
    const context = getCanvasContext(canvas);
    context.drawImage(this.canvas, 0, 0, this.width, this.height, 0, 0, 1, 1);
    return Array.prototype.slice.call(
      context.getImageData(0, 0, 1, 1).data
    ) as unknown as Color;
  }

  /**
   * @inheritDoc
   */
  protected resize(width: number, height: number): Canvas {
    const dst = makeCanvas(width, height, !this.isOffscreen());
    getCanvasContext(dst).drawImage(
      this.canvas,
      0,
      0,
      this.width,
      this.height,
      0,
      0,
      width,
      height
    );
    return new Canvas(dst);
  }

  /**
   * @inheritDoc
   */
  getResource(): BrowserCanvas {
    return this.canvas;
  }

  /**
   * @inheritDoc
   */
  protected prepareBlank(width: number, height: number): Canvas {
    return new Canvas(makeCanvas(width, height, !this.isOffscreen()));
  }

  /**
   * @inheritDoc
   */
  commit(): void {
    getCanvasContext(this.canvas).putImageData(this.imageData, 0, 0);
  }

  /**
   * Returns true if underlying canvas resource is OffscreenCanvas
   */
  isOffscreen(): boolean {
    return (
      typeof OffscreenCanvas !== undefined &&
      this.canvas instanceof OffscreenCanvas
    );
  }
}
